import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
} from "@mui/material";
import Breadcrumb from "../DefaultComponent/BreadCrumb";
import Spinner from "../DefaultComponent/Spinner";
import { ContestList } from "../../Redux/Actions/ContestAction";
import { ContestFilter } from "../../utils/Json";
import {
  formatDate,
  calculateTotalPages,
  formatDuration,
} from "../../utils/Helper";

const Contests: React.FC = () => {
  const dispatch = useDispatch<any>();
  const ContestListData = useSelector((state: any) => state.ContestListData);
  const [contestList, setContestList] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchBy, setSearchBy] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [ordering, setOrdering] = useState<string>("");
  const [testType, setTestType] = useState<string>("");
  const [testStatus, setTestStatus] = useState<string>("");
  const rowsPerPage = 12;

  const buildQueryString = () => {
    const params: Record<string, string> = {
      page: String(page),
      page_size: String(rowsPerPage),
    };
    if (searchBy && search.trim()) params[searchBy] = search.trim();
    if (from) params.from = from;
    if (to) params.to = to;
    if (ordering) params.ordering = ordering;
    if (testType) params.test_type = testType;
    if (testStatus) params.test_status = testStatus;
    return `?${new URLSearchParams(params).toString()}`;
  };

  const fetchData = () => {
    setLoading(true);
    dispatch(ContestList(buildQueryString()));
  };

  useEffect(fetchData, [
    dispatch,
    page,
    searchBy,
    search,
    from,
    to,
    ordering,
    testType,
    testStatus,
  ]);

  useEffect(() => {
    if (ContestListData?.status) {
      if (ContestListData?.data?.row) {
        setLoading(false);
        setContestList(ContestListData.data.row);
        setCount(ContestListData.data.total_count);
      }
    } else {
      setLoading(false);
      setContestList([]);
    }
  }, [ContestListData]);

  const applyFilters = () => {
    setPage(1);
    fetchData();
  };
  const clearFilters = () => {
    setSearchBy("");
    setSearch("");
    setFrom("");
    setTo("");
    setOrdering("");
    setTestType("");
    setTestStatus("");
    setPage(1);
    setTimeout(fetchData, 0);
  };

  return (
    <>
      {loading && <Spinner />}

      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Contests"
            breadcrumbs={[
              { label: "Contests", path: "/contests", isActive: true },
            ]}
          />
        </div>

        <Box sx={{ m: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
          <TextField
            select
            label="Search By"
            value={searchBy}
            onChange={(e) => {
              setSearchBy(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {ContestFilter.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            size="small"
          />

          <TextField
            label="From"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            size="small"
          />

          <TextField
            label="To"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            size="small"
          />

          <TextField
            select
            label="Ordering"
            value={ordering}
            onChange={(e) => {
              setOrdering(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
            <MenuItem value="newest">Newest</MenuItem>
          </TextField>

          <TextField
            select
            label="Test Type"
            value={testType}
            onChange={(e) => {
              setTestType(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="PUBLIC">Public</MenuItem>
            <MenuItem value="PRIVATE">Private</MenuItem>
            <MenuItem value="CONTEST">Contest</MenuItem>
          </TextField>

          <TextField
            select
            label="Test Status"
            value={testStatus}
            onChange={(e) => {
              setTestStatus(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="ONGOING">Ongoing</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="UPCOMING">Upcoming</MenuItem>
          </TextField>

          <Button variant="contained" onClick={applyFilters}>
            Apply Filters
          </Button>
          <Button variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
        <Box sx={{ m: 2, overflowX: "auto" }}>
          <TableContainer component={Paper}>
            <Table aria-label="Contests Table">
              <TableHead sx={{ backgroundColor: "#0555c8" }}>
                <TableRow>
                  <TableCell sx={{ color: "white" }}>S.No</TableCell>
                  <TableCell sx={{ color: "white" }}>Title</TableCell>
                  <TableCell sx={{ color: "white" }}>Organized By</TableCell>
                  <TableCell sx={{ color: "white" }}>MCQ</TableCell>
                  <TableCell sx={{ color: "white" }}>Coding</TableCell>
                  <TableCell sx={{ color: "white" }}>Total Coins</TableCell>
                  <TableCell sx={{ color: "white" }}>Duration</TableCell>
                  <TableCell sx={{ color: "white" }}>Code</TableCell>
                  <TableCell sx={{ color: "white" }}>Start Date</TableCell>
                  <TableCell sx={{ color: "white" }}>End Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contestList.length > 0 ? (
                  contestList.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                      <TableCell>{c.title}</TableCell>
                      <TableCell>{c.org_id?.org_name}</TableCell>
                      <TableCell>{c.total_mcq}</TableCell>
                      <TableCell>{c.total_coding}</TableCell>
                      <TableCell>{c.total_coins}</TableCell>
                      <TableCell>{formatDuration(c.test_duration)}</TableCell>
                      <TableCell>{c.test_code}</TableCell>
                      <TableCell>{formatDate(c.start_at)}</TableCell>
                      <TableCell>{formatDate(c.expired_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No Contests Found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ m: 2, display: "flex", justifyContent: "center" }}>
          <Stack spacing={2}>
            <Pagination
              count={calculateTotalPages(count, rowsPerPage)}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Stack>
        </Box>
      </div>
    </>
  );
};

export default Contests;
